import React from "react";
import { MessageCircle, FileText, ChevronRight } from "lucide-react";
import { buildProductWaLink } from "@/lib/whatsapp";
import { SPECIES, PRODUCT_CATEGORIES } from "@/data/products";
import SafeImage from "./SafeImage";

const speciesLabel = (id) => SPECIES.find((s) => s.id === id)?.label || id;
const categoryLabel = (id) => PRODUCT_CATEGORIES.find((c) => c.id === id)?.label || id;

export default function ProductCard({ product }) {
  return (
    <article
      className="card-rise bg-white border border-bah-line rounded-2xl overflow-hidden flex flex-col"
      data-testid={`product-card-${product.id}`}
    >
      {/* Catalogue page image (or branded placeholder when no catalogue page) */}
      {product.image ? (
        <div className="relative aspect-[4/3] bg-bah-paperAlt overflow-hidden border-b border-bah-line">
          <SafeImage
            src={product.image}
            alt={`${product.name} — Biodeal Animal Health catalogue page`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>
      ) : (
        <div className="relative aspect-[4/3] bg-gradient-to-br from-bah-green to-bah-greenDeep grid place-content-center text-center px-6 border-b border-bah-line">
          <div className="text-bah-yellow text-[10px] uppercase tracking-[0.22em] font-bold">
            {categoryLabel(product.category)}
          </div>
          <div className="mt-2 font-display text-white text-xl leading-tight">
            {product.name}
          </div>
          <div className="mt-3 text-white/70 text-[11px] uppercase tracking-wide">
            Product sheet available on request
          </div>
        </div>
      )}

      <div className="p-6 flex flex-col gap-4 flex-1">
        <header>
          <div className="text-[10px] uppercase tracking-[0.22em] text-bah-green font-bold">
            {categoryLabel(product.category)}
          </div>
          <h3 className="mt-1 font-display text-xl leading-tight text-bah-ink">
            {product.name}
          </h3>
        </header>

        <div className="flex flex-wrap gap-1.5">
          {product.species.map((s) => (
            <span
              key={s}
              className="text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-bah-yellow/40 text-bah-ink"
            >
              {speciesLabel(s)}
            </span>
          ))}
          {(product.badges || []).map((b) => (
            <span
              key={b}
              className="text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-bah-orange/10 text-bah-orange"
            >
              {b}
            </span>
          ))}
        </div>

        {product.active_ingredient && (
          <div className="text-sm">
            <div className="text-[11px] uppercase tracking-[0.18em] text-bah-mute font-bold">
              Active ingredient
            </div>
            <div className="mt-1 text-bah-ink">{product.active_ingredient}</div>
          </div>
        )}

        {product.use_summary && (
          <p className="text-sm leading-relaxed text-bah-mute">
            {product.use_summary}
          </p>
        )}

        {product.pack_sizes && (
          <div className="text-sm">
            <div className="text-[11px] uppercase tracking-[0.18em] text-bah-mute font-bold">
              Pack sizes
            </div>
            <div className="mt-1 text-bah-ink">{product.pack_sizes}</div>
          </div>
        )}

        {product.field_note && (
          <div className="text-sm bg-bah-paperAlt rounded-lg p-3 border border-bah-line">
            <div className="text-[11px] uppercase tracking-[0.18em] text-bah-green font-bold mb-1">
              Field note
            </div>
            <div className="text-bah-ink">{product.field_note}</div>
          </div>
        )}

        {product.safety && (
          <div className="text-xs text-bah-mute italic border-l-2 border-bah-green pl-3">
            {product.safety}
          </div>
        )}

        <footer className="mt-auto pt-2 flex flex-col sm:flex-row gap-2">
          <a
            href={buildProductWaLink(product.name)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`product-ask-${product.id}`}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-bah-green text-white text-sm font-semibold px-4 py-2.5 hover:bg-bah-greenLight transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            Ask Technical Team
          </a>
          <a
            href={buildProductWaLink(`Product sheet request: ${product.name}`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`product-sheet-${product.id}`}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-bah-line text-bah-ink text-sm font-semibold px-4 py-2.5 hover:bg-bah-paperAlt transition-colors"
          >
            <FileText className="h-4 w-4" />
            Product Sheet
            <ChevronRight className="h-3.5 w-3.5" />
          </a>
        </footer>
      </div>
    </article>
  );
}
